import { createContext, useCallback, useContext, useState } from 'react';
import { X } from 'lucide-react';

interface Toast {
    id: number;
    message: string;
    type: 'error' | 'success';
}

interface ToastContextValue {
    toast: (message: string, type?: 'error' | 'success') => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export function useToast() {
    return useContext(ToastContext);
}

let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const toast = useCallback((message: string, type: 'error' | 'success' = 'error') => {
        const id = ++nextId;
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 5000);
    }, []);

    const dismiss = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            {toasts.length > 0 && (
                <div className="fixed bottom-20 left-1/2 z-[100] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4 lg:bottom-6">
                    {toasts.map((t) => (
                        <div
                            key={t.id}
                            className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm shadow-lg backdrop-blur-sm animate-in slide-in-from-bottom-2 ${
                                t.type === 'error'
                                    ? 'border-red-800 bg-red-950/90 text-red-200'
                                    : 'border-green-800 bg-green-950/90 text-green-200'
                            }`}
                        >
                            <span className="flex-1">{t.message}</span>
                            <button
                                onClick={() => dismiss(t.id)}
                                className="shrink-0 opacity-60 hover:opacity-100"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </ToastContext.Provider>
    );
}
